import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ticketService, categoryService, branchService, teamService, userService, regionService, statusService, equipmentService } from '../services/api';
import { Card, Button, Input, Textarea, Select, MultiSelect, NormalMultiSelect, DatePicker, FileUpload, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { useSidebar } from '../context/SidebarContext';
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';
import { formatDateTimeDDMMYYYY } from '../utils/dateUtils';

const CreateEditTicket = ({ mode = 'view' }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const { user } = useAuth();
  const dept = useDepartment();
  const [availableEquipment, setAvailableEquipment] = useState([]);

  // BioMedical raises tickets against a device, so the form shows Equipment
  // and treats Subject as optional. It also hides the assignee: a coordinator
  // picks the engineer afterwards, so the person reporting the fault is not
  // asked to choose one.
  // BioMedical raises tickets against a device: it shows Equipment, and drops
  // the free-text Description, the Teams picker and the assignee. Subject stays
  // required in both departments.
  const isBioMedical = (user?.department_code || 'IT') === 'BM';
  const descriptionRequired = !isBioMedical;
  const showCategoryType = !isBioMedical;
  const showTeams = !isBioMedical;
  // BioMedical has no teams to route through, so a ticket reaches an agent only
  // by someone assigning it. Coordinators and admins carry can_assign_tickets;
  // for everyone else in BM the picker stays hidden. IT keeps its team-driven
  // assignment, where the field is always present.
  const canAssignTickets = !!user?.can_assign_tickets;
  const showAssignee = !isBioMedical || canAssignTickets;
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category_type: '', // Add category type
    category_id: '', // Changed to single select
    region_ids: [], // Multi-select for regions
    branch_ids: [],  // Multi-select for branches
    team_ids: [], // Changed to array for multi-select
    assigned_user_id: '', // Single user to assign
    equipment_id: '',     // BioMedical: device the fault is on
    manufacturer: '',     // prefilled from the equipment master
    model: '',
    serial_number: '',
    priority: 'P2',
    attachments: [] // Array of file objects
  });
  
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]); // Teams filtered by selected categories and region
  const [availableUsers, setAvailableUsers] = useState([]); // Users filtered by selected team
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(mode === 'create' || mode === 'edit');

  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    if (isViewMode) {
      setPageTitle('View Ticket');
    } else if (mode === 'edit') {
      setPageTitle('Edit Ticket');
    } else if (mode === 'create') {
      setPageTitle('Create Ticket');
    } else {
      setPageTitle('Ticket Details');
    }
    
    fetchDropdowns();
    // Don't fetch all teams initially - will be fetched based on category selection
    // fetchAllTeams();
    if (id) {
      fetchTicket();
    }
  }, [id, mode]); // Removed setPageTitle from dependencies

  // Refresh dropdowns when switching to edit mode
  useEffect(() => {
    const refreshDropdownsForEditMode = async () => {
      if (mode === 'edit' && id && formData.region_ids && formData.region_ids.length > 0) {
        // Re-fetch all dropdown options when entering edit mode
        console.log('Refreshing dropdowns for edit mode...');
        
        if (formData.region_ids.length > 0) {
          console.log('Fetching branches for regions:', formData.region_ids);
          await fetchBranchesByRegions(formData.region_ids);
        }
        
        if (formData.category_id && formData.region_ids.length > 0) {
          console.log('Fetching teams for category:', formData.category_id, 'regions:', formData.region_ids);
          await fetchTeamsByCategoriesAndRegion(formData.category_id, formData.region_ids[0]);
        }
        
        if (formData.team_ids && formData.team_ids.length > 0) {
          console.log('Fetching users for teams:', formData.team_ids);
          await fetchUsersByTeams(formData.team_ids);
        }
      }
    };
    
    refreshDropdownsForEditMode();
  }, [mode, formData.category_id, JSON.stringify(formData.region_ids)]);

  // Watch for category and region changes to fetch teams
  useEffect(() => {
    // Only fetch when in edit/create mode
    if (!isEditMode) return;
    
// Watch for category + region changes to fetch teams – use first region
      const categoryId = formData.category_id;
      const regionId = formData.region_ids && formData.region_ids.length > 0 ? formData.region_ids[0] : null;
    
    if (categoryId && regionId) {
      // Call teams API with selected category code and region
      fetchTeamsByCategoriesAndRegion(categoryId, regionId);
    } else {
      // Clear teams if conditions not met
      setAvailableTeams([]);
    }
  }, [formData.category_id, formData.region_ids, isEditMode]);

  // Watch for region changes to fetch branches
  useEffect(() => {
    // Only fetch when in edit/create mode
    if (!isEditMode) return;
    
    if (formData.region_ids && formData.region_ids.length > 0) {
      fetchBranchesByRegions(formData.region_ids);
    } else {
      setBranches([]);
    }
  }, [JSON.stringify(formData.region_ids), isEditMode]);


  
  // The chosen branches, as a stable string for dependency arrays. Declared
  // here because both the agent list below and the equipment list further down
  // key off it.
  const branchKey = (formData.branch_ids || []).join(',');

  // Watch for team changes to fetch users
  useEffect(() => {
    // Only fetch when in edit/create mode
    if (!isEditMode) return;

    // BioMedical routes through a coordinator rather than a team, so the
    // candidates are the department's agents. assignable=true asks the server
    // which roles may hold a ticket, rather than this file knowing role ids —
    // they differ between databases. The endpoint is department-scoped already.
    if (isBioMedical) {
      if (!showAssignee) return;
      // A ticket belongs to a branch, so only agents who cover that branch are
      // offered. With no branch chosen yet the list stays empty rather than
      // showing people who may not be eligible once one is picked.
      if (!branchKey) { setAvailableUsers([]); return; }
      let cancelled = false;
      userService.getAll({ limit: 500, assignable: 'true', branch_codes: branchKey })
        .then((res) => {
          if (cancelled) return;
          const rows = res.data?.data || [];
          setAvailableUsers(rows.filter((u) => u.status !== false));
          // Drop an assignee who no longer covers the chosen branch
          setFormData((prev) => (
            prev.assigned_user_id && !rows.some((u) => String(u.user_id) === String(prev.assigned_user_id))
              ? { ...prev, assigned_user_id: '' }
              : prev
          ));
        })
        .catch(() => { if (!cancelled) setAvailableUsers([]); });
      return () => { cancelled = true; };
    }

    if (formData.team_ids && formData.team_ids.length > 0) {
      fetchUsersByTeams(formData.team_ids);
    } else {
      setAvailableUsers([]);
    }
  }, [JSON.stringify(formData.team_ids), isEditMode, isBioMedical, showAssignee, branchKey]);

  
  // Ensure selected category is always in the categories list and prioritized
  useEffect(() => {
    if (formData.category_id && formData.category_name && categories.length > 0) {
      const selectedCategoryExists = categories.some(cat => cat.code === formData.category_id);
      if (!selectedCategoryExists) {
        // Add the selected category to the list
        setCategories(prev => [{
          id: null,
          code: formData.category_id,
          name: formData.category_name,
          status: true
        }, ...prev]);
      } else {
        // Move the selected category to the top
        setCategories(prev => {
          const selectedIndex = prev.findIndex(cat => cat.code === formData.category_id);
          if (selectedIndex > 0) {
            const newCategories = [...prev];
            const selectedCategory = newCategories.splice(selectedIndex, 1)[0];
            return [selectedCategory, ...newCategories];
          }
          return prev;
        });
      }
    }
  }, [formData.category_id, formData.category_name, categories.length]);


  // BioMedical: equipment follows the chosen category and branch, the same way
  // teams follow category + region. A device lives at one branch, so both
  // choices narrow the list — which is why they sit above it on the form.
  //
  // Branch is narrowed here rather than by the API because branch_ids is a
  // multi-select and the equipment endpoint takes a single branch_id.
  useEffect(() => {
    if (!isBioMedical || !isEditMode) return;

    let cancelled = false;
    equipmentService
      .getAll({ noPagination: 'true', category_code: formData.category_id || undefined })
      .then((res) => {
        if (cancelled) return;
        const selectedBranches = branchKey ? branchKey.split(',') : [];
        const rows = (res.data?.data || []).filter((e) => (
          selectedBranches.length === 0 ||
          // A device with no branch on record is unplaced rather than
          // elsewhere, so it stays selectable instead of vanishing.
          (!e.branch_id && !e.branch_code) ||
          selectedBranches.some((b) => String(b) === String(e.branch_id) ||
                                       String(b) === String(e.branch_code))
        ));
        setAvailableEquipment(rows);
        // Drop a device that no longer belongs to the chosen category or branch
        setFormData((prev) => (
          prev.equipment_id && !rows.some((e) => String(e.id) === String(prev.equipment_id))
            ? { ...prev, equipment_id: '', manufacturer: '', model: '', serial_number: '' }
            : prev
        ));
      })
      .catch(() => { if (!cancelled) setAvailableEquipment([]); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category_id, branchKey, isBioMedical, isEditMode]);

  // Selecting a device fills in its manufacturer, model and serial, so the
  // person reporting the fault does not have to look them up.
  const handleEquipmentChange = (e) => {
    const equipmentId = e.target.value;
    const device = availableEquipment.find((eq) => String(eq.id) === String(equipmentId));
    setFormData((prev) => ({
      ...prev,
      equipment_id: equipmentId,
      manufacturer: device?.manufacturer || '',
      model: device?.model || '',
      serial_number: device?.serial_number || ''
    }));
    if (errors.equipment_id) setErrors((prev) => ({ ...prev, equipment_id: '' }));
  };

  const fetchDropdowns = async () => {
    try {
      // Fetch categories for the category dropdown
      try {
        console.log('Fetching categories...');
        const categoriesRes = await categoryService.getAll({ limit: 1000 }); // Get all categories
        console.log('Categories response:', categoriesRes);

        const allCategories = Array.isArray(categoriesRes.data.data)
          ? categoriesRes.data.data.filter(cat => cat.status !== false) // Include active categories
          : Array.isArray(categoriesRes.data)
          ? categoriesRes.data.filter(cat => cat.status !== false)
          : [];

        console.log('Filtered categories:', allCategories);
        setCategories(allCategories);
      } catch (categoryErr) {
        console.error('Error fetching categories:', categoryErr);
        setCategories([]);
      }
      
      // Fetch regions for the region dropdown
      try {
        console.log('Fetching regions...');
        const regionsRes = await regionService.getAll();
        console.log('Regions response:', regionsRes);
        console.log('Regions data:', regionsRes.data);
        
        const allRegions = Array.isArray(regionsRes.data.data) 
          ? regionsRes.data.data.filter(region => region.status === true)
          : Array.isArray(regionsRes.data)
          ? regionsRes.data.filter(region => region.status === true)
          : [];
        
        console.log('Filtered regions:', allRegions);
        setRegions(allRegions);
      } catch (regionErr) {
        console.error('Error fetching regions:', regionErr);
        setRegions([]);
      }

      // Fetch statuses for dynamic status colors
      try {
        console.log('Fetching statuses...');
        const statusesRes = await statusService.getAll({ page: 1, limit: 1000 });
        console.log('Statuses response:', statusesRes);
        setStatuses(statusesRes.data.data || statusesRes.data);
      } catch (statusErr) {
        console.error('Error fetching statuses:', statusErr);
        setStatuses([]);
      }
      
      // Initialize empty arrays - will be loaded based on selections
      setAvailableTeams([]);
      setBranches([]);
      setTeams([]);
      setAvailableUsers([]);
    } catch (err) {
      console.error('Error fetching dropdowns:', err);
      toast.error('Failed to load form options');
      // Set empty arrays on error
      setCategories([]);
      setRegions([]);
      setBranches([]);
      setTeams([]);
      setAvailableTeams([]);
      setAvailableUsers([]);
      setAvailableUsers([]);
    }
  };

  const searchCategories = useCallback(async (searchTerm) => {
    try {
      // Always use API for consistency
      const params = { limit: searchTerm.trim() ? 50 : 1000, dropdown: 'true' };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      // Filter by category type if selected
      if (formData.category_type) {
        params.category_type = formData.category_type;
      }
      
      const response = await categoryService.getAll(params);
      // Handle both response formats: 
      // - dropdown=true returns flat array (response.data is array)
      // - regular returns { data: [...], pagination: {...} }
      const rawCategories = Array.isArray(response.data.data) 
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      let results = rawCategories.map(cat => ({ value: cat.code, label: cat.name }));
      
      // If we have local categories and this is initial load, merge them to ensure consistency
      if (!searchTerm.trim() && categories.length > 0) {
        const apiCodes = new Set(results.map(r => r.value));
        const missingFromApi = categories.filter(cat => !apiCodes.has(cat.code));
        results = [...results, ...missingFromApi.map(cat => ({ value: cat.code, label: cat.name }))];
      }
      
      // If viewing a ticket and the selected category is not in results, add it
      if (formData.category_id && formData.category_name) {
        const categoryExists = results.some(cat => cat.value === formData.category_id);
        if (!categoryExists) {
          results.unshift({ value: formData.category_id, label: formData.category_name });
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error searching categories:', error);
      // Fallback to local categories if API fails
      if (!searchTerm.trim() && categories.length > 0) {
        return categories.map(cat => ({ value: cat.code, label: cat.name }));
      }
      return [];
    }
  }, [categories, formData.category_id, formData.category_name, formData.category_type]);

  const fetchCategories = async (type = '') => {
    try {
      const params = { limit: 1000 };
      if (type) {
        params.category_type = type;
      }
      const response = await categoryService.getAll(params);
      let allCategories = Array.isArray(response.data.data) 
        ? response.data.data.filter(cat => cat.status !== false)
        : Array.isArray(response.data)
        ? response.data.filter(cat => cat.status !== false)
        : [];
      
      // Ensure the currently selected category is included and prioritized
      if (formData.category_id && formData.category_name) {
        const selectedCategoryExists = allCategories.some(cat => cat.code === formData.category_id);
        if (!selectedCategoryExists) {
          // Add the selected category to the list
          allCategories.unshift({
            id: null, // We don't have the ID, but that's ok
            code: formData.category_id,
            name: formData.category_name,
            status: true
          });
        } else {
          // Move the selected category to the top
          const selectedIndex = allCategories.findIndex(cat => cat.code === formData.category_id);
          if (selectedIndex > 0) {
            const selectedCategory = allCategories.splice(selectedIndex, 1)[0];
            allCategories.unshift(selectedCategory);
          }
        }
      }
      
      setCategories(allCategories);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setCategories([]);
    }
  };

  const fetchAllTeams = async () => {
    try {
      const teamsRes = await teamService.getAll();
      const allTeams = Array.isArray(teamsRes.data.data) 
        ? teamsRes.data.data.filter(team => team.status === true)
        : [];
      setAvailableTeams(allTeams);
    } catch (err) {
      console.error('Error fetching all teams:', err);
      setAvailableTeams([]);
    }
  };

  const fetchTeamsByCategories = async (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0 || categoryIds[0] === '') {
      fetchAllTeams();
      return;
    }
    
    try {
      // API call with selected category codes - backend expects comma-separated string
      const response = await teamService.getTeamsByCategories(categoryIds);
      const teams = Array.isArray(response.data) ? response.data : [];
      // Update available teams with filtered results
      setAvailableTeams(teams);
    } catch (err) {
      console.error('Error fetching teams by categories:', err);
      setAvailableTeams([]);
    }
  };

  const fetchTeamsByCategoriesAndRegion = async (categoryId, regionId) => {
    if (!categoryId || !regionId) {
      setAvailableTeams([]);
      return;
    }
    
    try {
      // Get region code from region id
      const selectedRegion = regions.find(r => r.id === regionId);
      const regionCode = selectedRegion?.code;
      
      if (!regionCode) {
        console.warn('Region code not found for region id:', regionId);
        setAvailableTeams([]);
        return;
      }
      
      console.log('Fetching teams by category:', categoryId, 'and region:', regionCode);
      
      // Call backend API to get filtered teams
      const response = await teamService.getTeamsByRegionAndCategory(regionCode, [categoryId]);
      const teams = Array.isArray(response.data) ? response.data : [];
      
      console.log('Filtered teams:', teams);
      setAvailableTeams(teams);
    } catch (err) {
      console.error('Error fetching teams by categories and region:', err);
      setAvailableTeams([]);
    }
  };

  const fetchBranchesByRegions = async (regionIds) => {
    if (!regionIds || regionIds.length === 0) {
      setBranches([]);
      return;
    }
    
    try {
      // Get region codes from IDs
      const regionCodes = regionIds
        .map(rId => regions.find(r => r.id === rId)?.code)
        .filter(Boolean);
      
      if (regionCodes.length === 0) {
        setBranches([]);
        return;
      }
      
      console.log('Fetching branches for region_codes:', regionCodes);
      const response = await branchService.getByRegions(regionCodes.join(','));
      const branchesData = Array.isArray(response.data.data) ? response.data.data : [];
      console.log('Fetched branches:', branchesData);
      setBranches(branchesData);
    } catch (err) {
      console.error('Error fetching branches by regions:', err);
      setBranches([]);
    }
  };

  const fetchUsersByTeams = async (teamIds) => {
    if (!teamIds || teamIds.length === 0) {
      setAvailableUsers([]);
      return;
    }
    
    try {
      console.log('Fetching users for team_codes:', teamIds);
      
      // Call backend API to get users for the teams
      const response = await userService.getByTeams(teamIds);
      const usersData = Array.isArray(response.data.data) ? response.data.data : [];
      
      console.log('Fetched users:', usersData);
      setAvailableUsers(usersData);
    } catch (err) {
      console.error('Error fetching users by teams:', err);
      setAvailableUsers([]);
    }
  };

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await ticketService.getById(id);
      const ticket = response.data;
      
      // Extract team codes and user IDs from assigned arrays
      const teamCodes = ticket.assigned_teams?.map(t => t.team_code) || [];
      const assignedUserId = ticket.assigned_user?.user_id || (ticket.assigned_users?.[0]?.user_id) || '';
      
      // Filter attachments to only show those created within 5 minutes of ticket creation
      // (these are considered "creation attachments")
      const ticketCreatedTime = new Date(ticket.created_at).getTime();
      const creationAttachments = ticket.attachments?.filter(att => {
        const attachmentCreatedTime = new Date(att.created_at).getTime();
        const timeDiff = Math.abs(attachmentCreatedTime - ticketCreatedTime);
        // Consider attachments created within 5 minutes as "creation attachments"
        return timeDiff <= 5 * 60 * 1000; // 5 minutes in milliseconds
      }) || [];
      
      setFormData({
        subject: ticket.subject || '',
        description: ticket.description || '',
        category_type: ticket.category_type || '',
        category_id: ticket.category_code || '',
        category_name: ticket.category_name || '',
        // The Branch dropdown is keyed by code, so prefer the code here —
        // mapping to branch_id leaves the saved branch unmatched and the
        // field looks empty on edit.
        branch_ids: (ticket.branches && ticket.branches.length > 0)
          ? ticket.branches.map(b => b.branch_code || b.branch_id)
          : (ticket.branch_code ? [ticket.branch_code] : []),
        branch_name: ticket.branch_name || '',
        region_ids: (ticket.regions && ticket.regions.length > 0)
          ? ticket.regions.map(r => r.region_id)
          : (ticket.region_id ? [ticket.region_id] : []),
        region_name: ticket.region_name || '',
        team_ids: teamCodes,
        assigned_user_id: assignedUserId,
        // BioMedical device fields. These are a snapshot taken when the ticket
        // was raised, so they are read back off the ticket, not the equipment
        // master — the device may have been edited since.
        equipment_id: ticket.equipment_id ? String(ticket.equipment_id) : '',
        manufacturer: ticket.manufacturer || '',
        model: ticket.model || '',
        serial_number: ticket.serial_number || '',
        priority: ticket.priority || 'P2',
        status: ticket.status || 'Open',
        attachments: [],
        assigned_teams: ticket.assigned_teams || [],
        assigned_users: ticket.assigned_users || []
      });
      
      // Filter categories by type if category_type exists
      if (ticket.category_type) {
        fetchCategories(ticket.category_type);
      } else if (ticket.category_code) {
        // If no category_type but category exists (e.g. email-created tickets),
        // fetch all categories so the dropdown shows the current selection
        fetchCategories();
      }
      
      // Set only creation attachments for edit mode display
      setAttachments(creationAttachments);
      
      // In edit mode, fetch all available options for dropdowns
      if (mode === 'edit') {
        // Fetch branches for the ticket's region if region exists
        if (ticket.region_id || (ticket.regions && ticket.regions.length > 0)) {
          const regionIds = (ticket.regions && ticket.regions.length > 0)
            ? ticket.regions.map(r => r.region_id)
            : [ticket.region_id];
          fetchBranchesByRegions(regionIds);
        }
        
        // Fetch teams for the ticket's category and region if both exist
        if (ticket.category_code && ticket.region_id) {
          fetchTeamsByCategoriesAndRegion(ticket.category_code, ticket.region_id);
        }
        
        // Fetch users for the ticket's teams if teams exist
        if (teamCodes.length > 0) {
          fetchUsersByTeams(teamCodes);
        }
      } else {
        // In view mode, populate dropdowns with only the assigned data
        // Populate available teams dropdown with assigned teams
        if (ticket.assigned_teams && ticket.assigned_teams.length > 0) {
          const teamsForDropdown = ticket.assigned_teams.map(t => ({
            code: t.team_code,
            name: t.team_name,
            status: true
          }));
          setAvailableTeams(teamsForDropdown);
        }
        
        // Populate available users dropdown with assigned users
        if (ticket.assigned_users && ticket.assigned_users.length > 0) {
          const usersForDropdown = ticket.assigned_users.map(u => ({
            user_id: u.user_id,
            name: u.user_name,
            email: u.user_email
          }));
          setAvailableUsers(usersForDropdown);
        }
        
        // Populate branches dropdown if branch exists
        if (ticket.branches && ticket.branches.length > 0) {
          setBranches(ticket.branches.map(b => ({ code: b.branch_code, name: b.branch_name })));
        } else if (ticket.branch_code && ticket.branch_name) {
          setBranches([{
            code: ticket.branch_code,
            name: ticket.branch_name
          }]);
        }
      }
      
      setComments(ticket.comments || []);
      setHistory(ticket.history || []);
      
      // For view mode, show all attachments
      if (mode === 'view') {
        setAttachments(ticket.attachments || []);
      }
      // For edit mode, attachments are already set above (only creation attachments)
    } catch (err) {
      console.error('Error fetching ticket:', err);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle file attachments
    if (name === 'attachments') {
      setFormData(prev => ({
        ...prev,
        attachments: value // value is array of File objects from FileUpload
      }));
      return;
    }
    
    // Handle cascading dropdowns - clear dependent fields
    if (name === 'category_type') {
      setFormData(prev => ({
        ...prev,
        category_type: value,
        category_id: '',
        team_ids: [],
        assigned_user_id: ''
      }));
      // Fetch categories filtered by the selected type
      if (value) {
        fetchCategories(value);
      } else {
        setCategories([]);
      }
    } else if (name === 'category_id') {
      setFormData(prev => ({
        ...prev,
        category_id: value,
        team_ids: [],
        assigned_user_id: ''
      }));
    } 
    else if (name === 'region_ids') {
      // Clear branch, team, and users when regions change
      setFormData(prev => ({
        ...prev,
        region_ids: value,
        branch_ids: [],
        team_ids: [],
        assigned_user_id: ''
      }));
    } 
    else if (name === 'team_ids') {
      // Clear users when teams change
      setFormData(prev => ({
        ...prev,
        team_ids: value,
        assigned_user_id: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (isBioMedical && !formData.equipment_id) {
      newErrors.equipment_id = 'Equipment is required';
    }
    
    if (descriptionRequired && !formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }
    
    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      
      // Convert arrays to single values for backend compatibility
      const submitData = {
        ...formData,
        category_id: formData.category_id || '',
        region_ids: formData.region_ids || [],
        branch_ids: formData.branch_ids || [],
        // Also send first value for backward-compat single-column reads
        region_id: formData.region_ids && formData.region_ids.length > 0 ? formData.region_ids[0] : null,
        branch_id: formData.branch_ids && formData.branch_ids.length > 0 ? formData.branch_ids[0] : null,
        team_ids: formData.team_ids || [],
        team_ids: showTeams ? formData.team_ids : [],
        assigned_user_id: formData.assigned_user_id || '',
        equipment_id: formData.equipment_id || null,
      };

      // Someone without the assignee field must not send the key at all.
      // Sending '' would read as "clear the assignee" and be rejected as an
      // unauthorised reassignment on a ticket that already has an agent.
      if (!showAssignee) {
        delete submitData.assigned_user_id;
      }

      if (id) {
        await ticketService.update(id, submitData);
        toast.success('Ticket updated successfully');
      } else {
        await ticketService.create(submitData);
        toast.success('Ticket created successfully');
      }
      
      navigate('/tickets');
    } catch (err) {
      console.error('Error saving ticket:', err);
      toast.error(err.response?.data?.error || 'Failed to save ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // Set edit mode - the useEffect will handle refreshing dropdowns
    setIsEditMode(true);
    navigate(`/tickets/edit/${id}`);
  };

  const handleCancel = () => {
    
      navigate('/tickets');
    
  };

  const getStatusBadge = (statusName) => {
    // Find the status in our statuses array
    const status = statuses.find(s => s.name === statusName);
    
    if (status && status.color) {
      // Convert hex color to Tailwind-like classes
      const color = status.color;
      // For now, return a style-based badge with the dynamic color
      return {
        backgroundColor: color,
        color: getContrastColor(color),
        padding: '0.25rem 0.75rem',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        display: 'inline-block',
        width: '100px', // Fixed width for consistent sizing
        textAlign: 'center'
      };
    }
    
    // Fallback to hardcoded colors if status not found or no color
    const statusColors = {
      'Open': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800',
      'Reopened': 'bg-red-100 text-red-800'
    };
    return statusColors[statusName] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to determine text color based on background color
  const getContrastColor = (hexColor) => {
    // Remove # if present
    const color = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // IT picks a Category Type before a Category, to narrow Software vs Hardware.
  // BioMedical has no such split, so its Category must not wait on one.
  const awaitingCategoryType = showCategoryType && !formData.category_type;

  // Region/Branch and Category are the same fields for both departments, but
  // BioMedical asks for them in the other order: equipment is filtered by
  // branch and category, so both have to be chosen before the device list
  // below makes sense. IT keeps its established order.
  const regionBranchFields = (
    <>
      {/* Region and Branch */}
      <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {dept.region}
          </label>
          <NormalMultiSelect
            name="region_ids"
            value={formData.region_ids}
            onChange={handleChange}
            options={Array.isArray(regions) ? regions.map(region => ({ value: region.id, label: region.name })) : []}
            placeholder={`Select ${dept.regions.toLowerCase()}`}
            disabled={!isEditMode}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <NormalMultiSelect
            name="branch_ids"
            value={formData.branch_ids}
            onChange={handleChange}
            options={Array.isArray(branches) ? branches.map(branch => ({ value: branch.code, label: branch.name })) : []}
            placeholder={!formData.region_ids || formData.region_ids.length === 0 ? `Select ${dept.regions.toLowerCase()} first` : 'Select branches'}
            disabled={!isEditMode || !formData.region_ids || formData.region_ids.length === 0}
          />
        </div>
    </>
  );

  const categoryFields = (
    <>
      {/* Category Type and Category - First Row */}
      <div className="md:col-span-2">
        <div className={`grid grid-cols-1 ${showCategoryType ? 'md:grid-cols-2' : ''} gap-4`}>
          {/* Category Type — IT only (Software / Hardware split) */}
          {showCategoryType && <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Type <span className="text-red-500">*</span>
            </label>
            <Select
              name="category_type"
              value={formData.category_type}
              onChange={handleChange}
              disabled={!isEditMode}
              error={!!errors.category_type}
            >
              <option value="">Select type</option>
              <option value="Software">Software</option>
              <option value="Hardware">Hardware</option>
            </Select>
            {errors.category_type && (
              <p className="mt-1 text-sm text-red-600">{errors.category_type}</p>
            )}
          </div>}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
           <Select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              onSearch={searchCategories}
              options={
                formData.category_id && formData.category_name
                  ? [{ value: formData.category_id, label: formData.category_name }]
                  : []
              }
              disabled={!isEditMode || (awaitingCategoryType && !formData.category_id)}
              error={!!errors.category_id}
              placeholder={awaitingCategoryType && !formData.category_id ? "Select category type first" : "Search categories..."}
            />
            {errors.category_id && (
              <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="admin-panel">
      <Card compact={isCollapsed} allowOverflow={true}>
        <div className="p-6">
          

          {loading && mode !== 'create' ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-12 w-12 border-3 border-primary-600 border-t-transparent"></div>
                <p className="text-sm text-gray-600 font-medium">Loading ticket details...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <Breadcrumbs
                items={[
                  { label: 'Tickets', to: '/tickets' },
                  { label: mode === 'create' ? 'Create Ticket' : mode === 'edit' ? 'Edit Ticket' : 'View Ticket' }
                ]}
                className="mb-6"
              />

              {/* Form Fields in requested layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Subject - Full Width */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Enter ticket subject"
                    disabled={!isEditMode}
                    error={!!errors.subject}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                  {/* Description — required for IT, optional for BioMedical */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description {descriptionRequired && <span className="text-red-500">*</span>}
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter ticket description"
                    disabled={!isEditMode}
                    rows={6}
                    error={!!errors.description}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>

                {isBioMedical
                  ? <>{regionBranchFields}{categoryFields}</>
                  : <>{categoryFields}{regionBranchFields}</>}

                  {isBioMedical && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name of the equipment <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="equipment_id"
                        value={formData.equipment_id}
                        onChange={handleEquipmentChange}
                        disabled={!isEditMode || !formData.category_id}
                        error={!!errors.equipment_id}
                      >
                        <option value="">
                          {formData.category_id ? 'Select equipment' : 'Select a category first'}
                        </option>
                        {availableEquipment.map((eq) => (
                          // value as a string because equipment_id is held as
                          // one, and Select matches with ===; a number here
                          // never matches and the field shows its placeholder
                          // even though the device is set. Label is a single
                          // string so it is not stringified with commas.
                          <option key={eq.id} value={String(eq.id)}>
                            {`${eq.name}${eq.code ? ` (${eq.code})` : ''}`}
                          </option>
                        ))}
                      </Select>
                      {errors.equipment_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.equipment_id}</p>
                      )}
                    </div>

                    {/* Filled from the equipment master and read-only: these
                        describe the device, not the fault. */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                      <Input name="manufacturer" value={formData.manufacturer} placeholder="From equipment" readOnly disabled />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <Input name="model" value={formData.model} placeholder="From equipment" readOnly disabled />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Serial number</label>
                      <Input name="serial_number" value={formData.serial_number} placeholder="From equipment" readOnly disabled />
                    </div>
                  </>
                  )}

                {/* Team and Users — IT only; BioMedical routes via the coordinator */}
                {showTeams && <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to Teams
                  </label>
                  <NormalMultiSelect
                    name="team_ids"
                    value={formData.team_ids}
                    onChange={handleChange}
                    options={Array.isArray(availableTeams) ? availableTeams.map(team => ({ value: team.code, label: team.name })) : []}
                    placeholder="Select teams (choose category & region)"
                    disabled={!isEditMode || !formData.category_id || !formData.region_ids || formData.region_ids.length === 0}
                  />
                </div>}

                   {showAssignee && <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isBioMedical ? 'Assign to Agent' : 'Assign to User (Optional)'}
                  </label>
                  <Select
                    name="assigned_user_id"
                    value={formData.assigned_user_id}
                    onChange={handleChange}
                    disabled={!isEditMode || (!isBioMedical && formData.team_ids.length === 0)}
                  >
                    <option value="">
                      {isBioMedical ? 'Unassigned' : 'Auto-assign (least workload)'}
                    </option>
                    {Array.isArray(availableUsers) && availableUsers.map(user => (
                      <option key={user.user_id} value={user.user_id}>{user.name || user.email}</option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.assigned_user_id
                      ? 'Ticket will be assigned to selected user'
                      : isBioMedical
                        ? 'If empty, the ticket stays in the coordinator queue'
                        : 'If empty, system will auto-assign user with least workload'}
                  </p>
                </div>}

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    disabled={!isEditMode}
                    error={!!errors.priority}
                  >
                    <option value="P1">P1 - High</option>
                    <option value="P2">P2 - Medium</option>
                    <option value="P3">P3 - Low</option>
                  </Select>
                  {errors.priority && (
                    <p className="mt-1 text-sm text-red-600">{errors.priority}</p>
                  )}
                </div>

                <div></div>

              

                {/* Attachments - Full Width */}
                <div className="md:col-span-2">
                  {/* Display existing attachments in edit mode (read-only) */}
                  {isEditMode && mode === 'edit' && attachments.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Attachments (Uploaded at Creation)</h4>
                      <div className="space-y-2">
                        {attachments.map(att => (
                          <div key={att.id} className="flex items-center justify-between gap-2 p-3 border rounded bg-gray-50">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="text-sm font-medium">{att.filename}</span>
                              <span className="text-xs text-gray-500">({(att.filesize / 1024).toFixed(2)} KB)</span>
                            </div>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}${att.filepath.replace(/\\/g, '/').startsWith('/') ? '' : '/'}${att.filepath.replace(/\\/g, '/')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                            >
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Note: Attachments cannot be modified in edit mode. Use the ticket details page to add more files.</p>
                    </div>
                  )}
                  
                  {/* File upload only in create mode */}
                  {mode === 'create' && (
                    <FileUpload
                      label="Attachments"
                      name="attachments"
                      onChange={handleChange}
                      maxFiles={5}
                      maxImageSize={5}
                      maxFileSize={20}
                      acceptedFormats={[
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/zip'
                      ]}
                    />
                  )}
                </div>
              </div>

              {/* View Mode: Display Current Status, Comments, History */}
              {mode === 'view' && id && (
                <div className="mt-8 space-y-6">
                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Attachments</h3>
                      <div className="space-y-2">
                        {attachments.map(att => (
                          <div key={att.id} className="flex items-center gap-2 p-2 border rounded">
                            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-sm">{att.filename}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  {comments.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Comments</h3>
                      <div className="space-y-4">
                        {comments.map(comment => (
                          <div key={comment.id} className="border rounded p-4 bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-medium text-sm">{comment.user_name}</span>
                              <span className="text-xs text-gray-500">
                                {formatDateTimeDDMMYYYY(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comment.comment}</p>
                            {comment.is_internal && (
                              <span className="inline-block mt-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                Internal
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                </div>
              )}

              {/* Action Buttons */}
              {isViewMode ? (
                <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/tickets')}
                    className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => navigate(`/tickets/edit/${id}`)}
                    className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Edit Ticket
                  </Button>
                </div>
              ) : isEditMode ? (
                <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                    className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                    className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {loading ? 'Saving...' : id ? 'Update Ticket' : '+ Create Ticket'}
                  </Button>
                </div>
              ) : null}
            </form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CreateEditTicket;
